// PunchClockCard.js — Dashboard punch-in/out widget for employees
// Two layouts switched by breakpoint:
//   Mobile  (<600px): compact centered card — date+geofence header, big mono
//            time, shift sub-label, full-width pill CTA.
//   Desktop (>=600px): high-density horizontal widget — time + pulsing status
//            dot and shift/geofence info on the left, compact CTA on the right.
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Box, Paper, Typography, CircularProgress, Snackbar, Alert } from '@mui/material';
import { Login as LoginIcon, Logout as LogoutIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { supabase, isSupabaseConfigured } from '../supabaseClient';
import { useAuth } from '../contexts/AuthContext';
import AppButton from './shared/AppButton';
import GeofenceChip from './shared/GeofenceChip';
import TodayStats from './shared/TodayStats';
import useIsMobile from './shared/useIsMobile';
import PunchConfirmDialog from './PunchConfirmDialog';
import { haversineM, isWithinAnyGeofence } from '../utils/attendanceCalculations';
import { savePunchOffline } from '../utils/offlineSync';
import db from '../db';

const pad2 = (n) => String(n).padStart(2, '0');
const getExpectedOutTime = (schedule) => ({ '9hours': '17:00', '10hours': '18:00', '12hours': '20:00', '12hours_sat': '20:00' }[schedule] || '18:00');
const MONO_FONT = 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace';

export default function PunchClockCard() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { userProfile } = useAuth();
  const currentEmployeeId = userProfile?.employee_id;
  const [now, setNow] = useState(new Date());
  const [employee, setEmployee] = useState(null);
  const [todayRow, setTodayRow] = useState(null);
  const [todayPunches, setTodayPunches] = useState([]);
  const [punchType, setPunchType] = useState('in');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [cooldown, setCooldown] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [gpsCoords, setGpsCoords] = useState(null);
  const [gpsError, setGpsError] = useState(null);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [geofenceZones, setGeofenceZones] = useState([]);
  const [punchEnabled, setPunchEnabled] = useState(true); // default true for backward compat

  useEffect(() => { const id = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(id); }, []);

  useEffect(() => {
    if (!isSupabaseConfigured() || !currentEmployeeId) return;
    (async () => {
      try {
        const { data } = await supabase.rpc('get_my_employee');
        if (data) { setEmployee({ id: currentEmployeeId, ...data }); setPunchEnabled(data.punch_enabled !== false); }
        else { const { data: emp } = await supabase.from('employees').select('id, employee_id, full_name, company_id, work_schedule, work_start_time, work_end_time, punch_enabled').eq('id', currentEmployeeId).maybeSingle(); if (emp) { setEmployee(emp); setPunchEnabled(emp.punch_enabled !== false); } }
      } catch (e) { console.error('[PunchClockCard] load employee:', e); }
    })();
  }, [currentEmployeeId]);

  useEffect(() => {
    if (!isSupabaseConfigured() || !employee?.company_id) return;
    (async () => {
      try {
        const { data } = await supabase.from('company_attendance_settings').select('geofence_locations, geofence_lat, geofence_lng, geofence_radius_m').eq('company_id', employee.company_id).maybeSingle();
        let zones = [];
        if (data?.geofence_locations?.length > 0) zones = data.geofence_locations;
        else if (data?.geofence_lat && data?.geofence_lng) zones = [{ name: 'Main Office', lat: data.geofence_lat, lng: data.geofence_lng, radius_m: data.geofence_radius_m || 200 }];
        setGeofenceZones(zones);
      } catch { setGeofenceZones([]); }
    })();
  }, [employee?.company_id]);

  useEffect(() => {
    if (!navigator.geolocation) return;
    setGpsLoading(true);
    const watchId = navigator.geolocation.watchPosition(
      (pos) => { setGpsCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }); setGpsError(null); setGpsLoading(false); },
      (err) => { setGpsError(err.message); setGpsLoading(false); },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 10000 }
    );
    return () => navigator.geolocation.clearWatch(watchId);
  }, []);

  const fetchToday = useCallback(async () => {
    if (!isSupabaseConfigured() || !currentEmployeeId) return;
    const today = new Date();
    const dateStr = `${today.getFullYear()}-${pad2(today.getMonth() + 1)}-${pad2(today.getDate())}`;
    try {
      const { data: punches } = await supabase.from('attendance_punches').select('id, type, punched_at').eq('employee_id', currentEmployeeId).eq('punch_date', dateStr).order('punched_at', { ascending: true });
      const p = punches || [];
      setTodayPunches(p);
      const ins = p.filter(x => x.type === 'in');
      const outs = p.filter(x => x.type === 'out');
      setPunchType(ins.length > outs.length ? 'out' : 'in');
      try { const pending = await db.pendingPunches.where('employee_id').equals(currentEmployeeId).filter(x => x.punch_date === dateStr).toArray(); if (pending.length > 0) { const allP = [...p, ...pending.map(x => ({ id: `pending-${x.id}`, type: x.type, punched_at: x.punched_at }))]; if (allP.filter(x => x.type === 'in').length > allP.filter(x => x.type === 'out').length) setPunchType('out'); } } catch {}
      const { data: dayRow } = await supabase.from('attendance_days').select('*').eq('employee_id', currentEmployeeId).eq('date', dateStr).maybeSingle();
      setTodayRow(dayRow);
    } catch (e) { console.error('[PunchClockCard] fetch today:', e); }
  }, [currentEmployeeId]);

  useEffect(() => { fetchToday(); }, [fetchToday]);

  const geofenceStatus = useMemo(() => {
    if (!gpsCoords || !geofenceZones.length) return { distance: null, nearestZone: null, inside: null };
    let nearestDist = Infinity, nearestZone = null;
    for (const z of geofenceZones) { const d = haversineM(gpsCoords.lat, gpsCoords.lng, parseFloat(z.lat), parseFloat(z.lng)); if (d !== null && d < nearestDist) { nearestDist = d; nearestZone = z; } }
    return { distance: nearestDist === Infinity ? null : Math.round(nearestDist), nearestZone, inside: isWithinAnyGeofence(gpsCoords.lat, gpsCoords.lng, geofenceZones) };
  }, [gpsCoords, geofenceZones]);

  const handlePunchConfirm = async (type, { photo, lat, lng, accuracy }) => {
    setSubmitting(true);
    try {
      const punchNow = new Date();
      const dateStr = `${punchNow.getFullYear()}-${pad2(punchNow.getMonth() + 1)}-${pad2(punchNow.getDate())}`;
      // Cap accuracy_m to 9999.99 (DB column is DECIMAL(6,2))
      const cappedAccuracy = accuracy != null ? Math.min(Math.round(accuracy * 100) / 100, 9999.99) : null;
      const punchData = { employee_id: currentEmployeeId, company_id: employee?.company_id || null, punch_date: dateStr, type, punched_at: punchNow.toISOString(), lat, lng, accuracy_m: cappedAccuracy, source: 'webapp', flagged: (lat === null && lng === null) ? 'no_gps' : null };
      if (navigator.onLine) {
        const { error } = await supabase.from('attendance_punches').insert(punchData);
        if (error) throw error;
        try { await supabase.rpc('compute_attendance_day', { p_employee_id: currentEmployeeId, p_date: dateStr }); } catch {}
      } else {
        await savePunchOffline(punchData);
        setSnackbar({ open: true, message: 'Punch saved offline — will sync when online', severity: 'info' });
      }
      if (navigator.vibrate) navigator.vibrate(200);
      setSnackbar({ open: true, message: `${type === 'in' ? 'Punched In' : 'Punched Out'} successfully!`, severity: 'success' });
      setConfirmOpen(false);
      setCooldown(true);
      setTimeout(() => setCooldown(false), 5000);
      await fetchToday();
    } catch (err) {
      console.error('[PunchClockCard] punch error:', err);
      setSnackbar({ open: true, message: `Punch failed: ${err.message}`, severity: 'error' });
    } finally { setSubmitting(false); }
  };

  const handlePunchTap = () => { if (!cooldown && !submitting) setConfirmOpen(true); };

  const clockStr = `${pad2(now.getHours())}:${pad2(now.getMinutes())}:${pad2(now.getSeconds())}`;
  const dateStrDisplay = now.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', weekday: 'short' });
  // Use employee's custom work_end_time if set, otherwise fall back to schedule default
  const expectedOut = employee?.work_end_time || getExpectedOutTime(employee?.work_schedule);

  // Determine if shift is truly complete: all ins have matching outs AND
  // the expected out time has passed (or there was never a punch-in).
  // If the employee punched out early (before expected out time), allow
  // re-punching in — they may have accidentally hit punch-out.
  const ins = todayPunches.filter(p => p.type === 'in');
  const outs = todayPunches.filter(p => p.type === 'out');
  const allMatchedOut = ins.length > 0 && ins.length <= outs.length;
  const [expHour, expMin] = expectedOut.split(':').map(Number);
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  const expMinutes = expHour * 60 + expMin;
  const isShiftTimeOver = nowMinutes >= expMinutes;
  // Only mark "done" if the shift time has actually ended
  const isDone = allMatchedOut && isShiftTimeOver;
  // If all matched out but shift isn't over yet, treat as early punch-out → allow re-punch-in
  const isEarlyOut = allMatchedOut && !isShiftTimeOver;

  const state = isDone ? 'done' : isEarlyOut ? 'in' : punchType;

  // Shift context
  const inPunch = todayPunches.find(p => p.type === 'in');
  const clockedInAt = inPunch ? new Date(inPunch.punched_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : null;
  const isActiveShift = state === 'out'; // clocked in, shift running
  const subLabel = isEarlyOut
    ? `Left early at ${outs.length > 0 ? new Date(outs[outs.length - 1].punched_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '?'}`
    : state === 'in' ? `Out by ${expectedOut}` : `Clocked in at ${clockedInAt}`;

  // CTA config per state (palette-driven; DONE is a disabled grey state)
  const ctaCfg = {
    in: { label: isEarlyOut ? 'PUNCH IN AGAIN' : 'PUNCH IN', main: 'success.main', hover: 'success.dark', Icon: LoginIcon, aria: isEarlyOut ? 'Punch in again' : 'Punch in' },
    out: { label: 'PUNCH OUT', main: 'error.main', hover: 'error.dark', Icon: LogoutIcon, aria: 'Punch out' },
    done: { label: 'Shift Completed Today', main: null, hover: null, Icon: null, aria: 'Shift completed today' },
  };
  const cta = ctaCfg[state];

  // Desktop geofence summary — text form of GeofenceChip's semantics
  const gf = (() => {
    if (gpsLoading) return { label: 'Locating…', color: 'text.secondary' };
    if (gpsError || !geofenceStatus || geofenceStatus.inside === null) return { label: 'No GPS', color: 'text.secondary' };
    const { inside, distance, nearestZone } = geofenceStatus;
    if (inside) return { label: nearestZone ? `${nearestZone.name} · ${distance}m` : `In zone · ${distance}m`, color: 'success.main' };
    const near = distance <= ((nearestZone?.radius_m || 200) * 2);
    return near
      ? { label: `Near ${nearestZone?.name || 'zone'} (${distance}m)`, color: 'warning.main' }
      : { label: `Outside geofence (${distance}m)`, color: 'error.main' };
  })();

  const disabledTap = state === 'done' || cooldown || submitting;

  // Don't render if punch is not enabled for this employee
  if (!punchEnabled && employee) return null;

  const ctaButton = (fullWidth) => (
    <Box
      onClick={disabledTap ? undefined : handlePunchTap}
      aria-label={cta.aria}
      role="button"
      tabIndex={state === 'done' ? -1 : 0}
      sx={{
        ...(fullWidth ? { mt: 2.5, height: 48, width: '100%' } : { height: 40, px: 2.5, flexShrink: 0 }),
        borderRadius: '6px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        fontSize: fullWidth ? '0.875rem' : '0.8125rem',
        fontWeight: 600,
        color: state === 'done' ? 'grey.400' : 'white',
        bgcolor: state === 'done' ? (fullWidth ? 'grey.100' : 'grey.200') : cta.main,
        cursor: disabledTap ? 'not-allowed' : 'pointer',
        opacity: cooldown ? 0.6 : 1,
        boxShadow: 1,
        transition: 'background-color 0.2s ease',
        userSelect: 'none',
        '&:hover': !disabledTap ? { bgcolor: cta.hover } : {},
      }}
    >
      {submitting ? (
        <CircularProgress size={fullWidth ? 22 : 18} sx={{ color: 'white' }} />
      ) : (
        <>
          {cta.Icon && <cta.Icon sx={{ fontSize: fullWidth ? 20 : 18 }} />}
          {cta.label}
        </>
      )}
    </Box>
  );

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 3,
        borderRadius: '10px',
        border: '1px solid',
        borderColor: 'grey.200',
        bgcolor: isMobile ? 'background.paper' : 'grey.50',
        p: isMobile ? 3 : 2,
        maxWidth: isMobile ? 448 : '100%',
        mx: 'auto',
      }}
    >
      {isMobile ? (
        /* ── MOBILE: compact centered card ─────────────────────────── */
        <Box sx={{ textAlign: 'center' }}>
          {/* Header row: date + geofence chip */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 1 }}>
            <Typography variant="caption" color="text.secondary">{dateStrDisplay}</Typography>
            <GeofenceChip geofenceStatus={geofenceStatus} gpsLoading={gpsLoading} gpsError={gpsError} />
          </Box>

          {/* Live digital time */}
          <Typography
            sx={{
              fontSize: '2.25rem',
              fontWeight: 700,
              letterSpacing: '-0.02em',
              color: 'grey.900',
              fontFamily: MONO_FONT,
              fontVariantNumeric: 'tabular-nums',
              lineHeight: 1.15,
            }}
          >
            {clockStr}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'grey.500', fontSize: '0.75rem' }}>
            {subLabel}
          </Typography>

          {/* Primary CTA (full width) */}
          {ctaButton(true)}

          <TodayStats todayRow={todayRow} expectedOut={expectedOut} />

          <Box sx={{ mt: 2 }}>
            <AppButton variant="ghost" size="small" onClick={() => navigate('/my-attendance')}>View Full Attendance →</AppButton>
          </Box>
        </Box>
      ) : (
        /* ── DESKTOP: high-density horizontal widget ───────────────── */
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          {/* Left: status info */}
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography
                sx={{
                  fontSize: '1.75rem',
                  fontWeight: 700,
                  letterSpacing: '-0.02em',
                  color: 'grey.900',
                  fontFamily: MONO_FONT,
                  fontVariantNumeric: 'tabular-nums',
                  lineHeight: 1.2,
                }}
              >
                {clockStr}
              </Typography>
              {/* Pulsing status dot: green = active shift, slate = off-duty */}
              <Box sx={{ position: 'relative', width: 8, height: 8, flexShrink: 0 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: isActiveShift ? 'success.main' : 'grey.400' }} />
                {isActiveShift && (
                  <Box
                    sx={{
                      position: 'absolute', top: 0, left: 0, width: 8, height: 8, borderRadius: '50%',
                      bgcolor: 'success.main',
                      animation: 'punchPulse 1.6s ease-out infinite',
                      '@keyframes punchPulse': {
                        '0%': { transform: 'scale(1)', opacity: 0.6 },
                        '100%': { transform: 'scale(2.6)', opacity: 0 },
                      },
                    }}
                  />
                )}
              </Box>
            </Box>
            {/* Row 2: out-by · geofence */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25, flexWrap: 'wrap' }}>
              <Typography variant="caption" color="text.secondary">Out by {expectedOut}</Typography>
              <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: 'grey.400' }} />
              <Typography variant="caption" sx={{ color: gf.color, fontWeight: 500 }}>{gf.label}</Typography>
            </Box>
          </Box>

          {/* Right: action */}
          {ctaButton(false)}
        </Box>
      )}

      <PunchConfirmDialog open={confirmOpen} onClose={() => setConfirmOpen(false)} onConfirm={handlePunchConfirm} punchType={punchType} geofenceZones={geofenceZones} />
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>{snackbar.message}</Alert>
      </Snackbar>
    </Paper>
  );
}
